import { useMemo } from 'react';
import { DateTime } from 'luxon';
import type { PermissionAction, PermissionGroupId } from '@strength/domain';
import { isPermissionDrivenRole } from '@strength/domain';
import { queries } from '@strength/zero-schema';
import { useAuth } from '../../../../auth';
import { useFlags } from '../../../context/featureFlag';
import { useIsZeroSurfaceActive } from '../../../zero/zero-active';
import {
  isZeroPermissionGranted,
  useZeroPermissions,
} from '../../../zero/useZeroPermissions';
import { useZeroQuery } from '../../../../zero/useZeroQuery';
import { getUrlTeam, useViewer } from '../../../context/viewer';
import { RouterOutput } from '../../../../trpc';
import { trpc } from '../../../../trpc/react';
import {
  transformCrossTeamZeroRows,
  type ZeroCrossTeamPhaseRow,
  type ZeroCrossTeamTrackRow,
} from './cross-team-zero';

type CrossTeamResult = RouterOutput['plan']['crossTeamTracksForAthlete'][0];

export type UseCrossTeamDataResult = {
  additionalTeamsData: CrossTeamResult[];
};

/**
 * Computes the date range for the cross-team tracks query.
 * Starts 1 month before the given date (start of month) and
 * ends 4 months after the given date (end of month).
 */
export function computeCrossTeamDateRange(timelineDate: DateTime): {
  startDate: string;
  endDate: string;
} {
  const startDate =
    timelineDate.minus({ months: 1 }).startOf('month').toISODate() ?? '';
  const endDate =
    timelineDate.plus({ months: 4 }).endOf('month').toISODate() ?? '';
  return { startDate, endDate };
}

export function useCrossTeamData({
  athleteProfileId,
  enabled,
  timelineDate,
  activeTeamId,
}: {
  athleteProfileId: string | null;
  enabled: boolean;
  timelineDate: DateTime;
  activeTeamId: string;
}): UseCrossTeamDataResult {
  const { startDate, endDate } = computeCrossTeamDateRange(timelineDate);

  // teamProcedure scoped solely by the URL-derived `tw-team-ids` header, so
  // enablement must gate on the same URL value — see the invariant in
  // app/context/viewer.tsx. The Plan tree stays mounted for a render after
  // navigating to a team-agnostic route; without this gate the query
  // refetches headerless and 400s when additional teams is on.
  const hasUrlTeam = getUrlTeam(window.location.pathname) !== '';

  // The tRPC-resolved viewer document — the gate source off the Zero path.
  // The route serves cross-team track rows with nested blocks, so both
  // governing Views must hold. The query must simply not fire when either
  // is revoked: the server denial is a route-permission error that the
  // query client turns into an error boundary.
  const { hasPermissions, team } = useViewer();

  const { showAdditionalTeamsFlag } = useFlags();
  const { auth } = useAuth();
  const isZeroActive = useIsZeroSurfaceActive('tracks');

  // SC-5583: the Zero path is chosen only when the tracks surface is active
  // AND the session is org-wide (`auth.teamIds === null`) — the same value the
  // server guard reads. It is NOT derived from role plus permission scope: the
  // role-sync fingerprint has no scope, so a cached scope can disagree with
  // the server guard. Team-restricted sessions always take the tRPC path with
  // its imperative pre-check, so the client never sends a guarded query from a
  // team-scoped session.
  const isOrgWideSession = auth?.type === 'org' && auth.teamIds === null;

  // SC-5685: on the Zero path the cross-team gates read the Zero-synced
  // permission document instead of the tRPC-resolved one — IVM re-evaluates
  // them live, so a mid-session revocation tears the subscriptions down and
  // hides the data without a reload. Legacy roles have no permission
  // document and stay granted (the whitelist governs them); permission-
  // driven roles fail closed while the document syncs or errors (SC-4768
  // discipline), so a revoked View suppresses both the Zero subscriptions
  // AND the tRPC fallback — a stale tRPC grant must not refetch into a
  // server-denied error boundary.
  const zeroGates = isZeroActive && isOrgWideSession && showAdditionalTeamsFlag;
  const zeroPermissions = useZeroPermissions();
  const isPermissionDriven =
    auth?.type === 'org' && isPermissionDrivenRole(auth.role);
  const zeroDocReady = zeroPermissions.status === 'ready';
  const hasView = (
    group: PermissionGroupId,
    action: PermissionAction,
  ): boolean => {
    if (zeroGates) {
      // Legacy roles have no permission document — always granted on this
      // path (the whitelist governs them). Permission-driven roles read the
      // Zero-synced document, failing closed while it syncs or errors.
      if (!isPermissionDriven) {
        return true;
      }
      return (
        zeroDocReady &&
        isZeroPermissionGranted(zeroPermissions.permissions, group, action)
      );
    }
    return hasPermissions(group, action);
  };
  const hasCrossTeamViews =
    hasView('plan.tracks', 'view') && hasView('plan.blocks', 'view');
  const canViewPhases = hasView('plan.phases', 'view');

  const zeroEligible =
    enabled &&
    zeroGates &&
    !!athleteProfileId &&
    hasUrlTeam &&
    hasCrossTeamViews;

  // Zero subscriptions — both hooks called unconditionally (Rules of Hooks);
  // the query is passed only when eligible, exactly like useTracksQuery.
  const tracksArgs = useMemo(
    () =>
      athleteProfileId
        ? { athleteProfileId, activeTeamId, startDate, endDate }
        : undefined,
    [athleteProfileId, activeTeamId, startDate, endDate],
  );
  // SAFETY: useZeroQuery returns rows of the named query's root table
  // ('Track') with its related blocks and nested layers, SC-5647-normalized —
  // structurally ZeroCrossTeamTrackRow.
  const [rawTracks, tracksDetails] = useZeroQuery(
    zeroEligible && tracksArgs
      ? queries.track.crossTeamTracksForAthlete(tracksArgs)
      : undefined,
  );
  const zeroTracks = rawTracks as ZeroCrossTeamTrackRow[] | undefined;

  // Phases are only rendered for teams that have tracks for the athlete, so
  // the phases query scopes to exactly those teams once the tracks result is
  // in. Subscribed only when Phases View is held — read from the Zero-synced
  // permission document on this path, so a mid-session revocation drops the
  // subscription live through IVM (F2): the tRPC path never filtered phases
  // server-side (the UI hides them), but under Zero the subscription must
  // simply not exist when the view is revoked.
  const phaseTeamIds = useMemo(
    () => (zeroTracks ? [...new Set(zeroTracks.map((t) => t.teamId))] : []),
    [zeroTracks],
  );
  const phasesArgs = useMemo(
    () => ({ teamIds: phaseTeamIds, startDate, endDate }),
    [phaseTeamIds, startDate, endDate],
  );
  // SAFETY: rows of the named query's root table ('Phase'), normalized.
  const [rawPhases, phasesDetails] = useZeroQuery(
    zeroEligible && canViewPhases && phaseTeamIds.length > 0
      ? queries.track.crossTeamPhases(phasesArgs)
      : undefined,
  );
  const zeroPhases = rawPhases as ZeroCrossTeamPhaseRow[] | undefined;

  const zeroError =
    tracksDetails.type === 'error' || phasesDetails.type === 'error';

  const zeroData = useMemo(() => {
    if (!zeroTracks || zeroError) return [];
    return transformCrossTeamZeroRows({
      tracks: zeroTracks,
      phases: zeroPhases ?? [],
      teams: team.teams,
    });
  }, [zeroTracks, zeroPhases, zeroError, team.teams]);

  const trpcQuery = trpc.plan.crossTeamTracksForAthlete.useQuery(
    // athleteProfileId is guaranteed non-null when the query is enabled
    // (enabled: enabled && !!athleteProfileId && hasUrlTeam ensures this)
    { athleteProfileId: athleteProfileId as string, startDate, endDate },
    {
      enabled:
        enabled &&
        !!athleteProfileId &&
        hasUrlTeam &&
        hasCrossTeamViews &&
        !zeroEligible,
      // 5 min fresh — caps refetches; access changes propagate on the next
      // window focus (default refetchOnWindowFocus picks them up once stale).
      staleTime: 5 * 60 * 1000,
      // 30 min in cache — keeps previously-fetched month windows around so
      // scroll-back across month boundaries serves from cache, not network.
      // (React Query v4 uses cacheTime; renamed to gcTime in v5.)
      cacheTime: 30 * 60 * 1000,
    },
  );

  // A disabled query still serves whatever is already in the cache, and the
  // 30-minute cacheTime keeps previously-fetched windows around — so after a
  // mid-session revocation `query.data` could hand back cross-team tracks and
  // blocks the viewer may no longer see. Suppress it at the hook boundary.
  // On the Zero path a denied query (e.g. after a mid-session scope change)
  // surfaces as an error detail, never a thrown render error — it degrades to
  // an empty state instead of an error boundary.
  return {
    additionalTeamsData: hasCrossTeamViews
      ? zeroEligible
        ? zeroData
        : (trpcQuery.data ?? [])
      : [],
  };
}
